/**
 * DE QUÉ COLOR VA CADA RESPUESTA DEL REPORTE MENSUAL, COMO EN CALMA (20-10).
 *
 * En Calma el color no lo decide el panel: viaja dentro de la respuesta. Cada opción del
 * formulario mensual se guardaba como «texto|LETRA|puntos» (por ejemplo «Máximo|A|10») y la
 * tabla del panel (`group-admin-miembros`, `parsearRespuestaEstilizadaConNota`) pinta la
 * celda con la clase `bg-<letra>`. Las cinco clases, en su CSS:
 *
 *   .bg-a { background-color: #0f0 }                 verde
 *   .bg-b { background-color: #ff0 }                 amarillo
 *   .bg-c { background-color: var(--color-marca) }   naranja (#ff6620)
 *   .bg-d { background-color: red }                  rojo
 *   .bg-i { background-color: #0dcaf0 }              azul
 *
 * A NUESTRA BASE LLEGARON SIN LA LETRA. `calma_respuestas` de cada reporte trae el texto
 * largo de la opción y nada más. La letra la pone el servidor (`backend/core/letras_de_calma.py`)
 * y la manda en `calma_letras`: la de su formulario de `calma_raw` si cruza y, si no, la de
 * su tabla única de respuestas (larga, corta y letra), que también da la corta
 * (`calma_cortas`) y el color del compromiso y la suplementación de los mensuales de la app.
 *
 * AQUÍ NO SE ADIVINA NADA POR EL TEXTO (21-10, revisor). Hasta hoy había aquí otra lista de
 * textos con su letra y la del servidor ya decía otra cosa en tres respuestas: la pantalla
 * pinta solo lo que manda el servidor. Una respuesta sin letra en `calma_letras` va sin color.
 *
 * Es cómo se pinta, no lo que vale: por eso las clases viven aquí.
 */

import { sinElRelleno } from './rellenoDelImport';

// Las cinco letras de Calma, intensas como allí y legibles sobre el fondo blanco del panel:
// el texto casi negro encima se lee bien sobre las cinco.
export const TONOS = {
    A: { nombre: 'Verde', clase: 'bg-[#2BD94F] text-[#1C1B19]' },
    B: { nombre: 'Amarillo', clase: 'bg-[#FFE11A] text-[#1C1B19]' },
    C: { nombre: 'Naranja', clase: 'bg-[#FF6620] text-[#1C1B19]' },
    D: { nombre: 'Rojo', clase: 'bg-[#F03A3A] text-[#1C1B19]' },
    I: { nombre: 'Azul', clase: 'bg-[#1CC8EE] text-[#1C1B19]' },
};

// QUÉ SE LE PREGUNTÓ EN CADA UNA (20-10). Francisco: con «Objetivo» arriba y «No» debajo
// «no se sabe qué le pregunto en realidad». En Calma la pregunta del objetivo era si cambiaba
// («No, mi prioridad sigue siendo...», letra A) y la del descanso si dormía mal («No, duermo
// sin problema», letra A): por eso ese «No» va en verde. El título dice la pregunta.
export const PREGUNTA_DEL_MENSUAL = {
    compromiso: '¿Cómo ha sido tu compromiso?',
    objetivo: '¿Cambia tu objetivo?',
    cumplimientoDieta: '¿Cómo has cumplido la dieta?',
    esfuerzoParaCumplirDieta: '¿Te ha costado la dieta?',
    tomaDeSuplementos: '¿Has tomado los suplementos?',
    suplementacion: '¿Has tomado los suplementos?',
    cumplimientoEntrenamiento: '¿Has cumplido el entreno?',
    cumplimientoCardio: '¿Has cumplido el cardio?',
    descanso: '¿Tienes problemas para dormir?',
    problemasParaEntrenar: '¿Problemas para entrenar?',
};

/**
 * La letra de Calma (A, B, C, D o I) de una respuesta de un reporte, la que mandó el
 * servidor en `calma_letras` (de Calma o de un mensual de la app); `null` si no la hay.
 */
export const letraDelReporte = (reporte, pregunta) => {
    const letra = (reporte?.calma_letras || {})[pregunta];
    return TONOS[letra] ? letra : null;
};

/**
 * LA RESPUESTA DE CALMA QUE SE ENSEÑA: LA CORTA (21-10). Jesús: «yo lo que hice fue
 * simplificar la respuesta... no poner la misma respuesta que está en calma». La corta la
 * manda el servidor en `calma_cortas` (`backend/core/letras_de_calma.py`); sin ella, la
 * larga de `calma_respuestas`. Las dos sin el relleno del volcado (`sinElRelleno`), así que
 * un «Sin calificar cumplimiento» corto deja ver la larga. `{ valor, larga }`, o los dos
 * `null` si no contestó.
 */
export const respuestaDelReporte = (reporte, pregunta) => {
    const larga = sinElRelleno((reporte?.calma_respuestas || {})[pregunta]);
    const corta = sinElRelleno((reporte?.calma_cortas || {})[pregunta]);
    return { valor: corta || larga, larga };
};

/** Las clases del fondo y el texto de una letra; `null` sin letra. */
export const claseDelTono = (letra) => (TONOS[letra] ? TONOS[letra].clase : null);
